import { initControls } from './controls';
import htmx from 'htmx.org';

const CONTENT_SELECTOR = '.profint-body';
const TITLE_SELECTOR = '.topbar-title';
const SIDEBAR_SELECTOR = '.profint-sidebar';
const NAVIGATING_CLASS = 'is-shell-navigating';
const EXPANDED_SECTIONS_KEY = 'profint.navigation.v2.expanded-sections';
const SIDEBAR_COLLAPSED_KEY = 'profint.navigation.v2.sidebar-collapsed';
const LEGACY_LICENSES_BASE_PATH = '/licencias-profesionales/turnos-online';
const LEGACY_LICENSES_HASH_ROUTES: Readonly<Record<string, string>> = {
  '#licencias-conciliacion': `${LEGACY_LICENSES_BASE_PATH}/pagos`,
  '#licencias-configuracion': `${LEGACY_LICENSES_BASE_PATH}/configuracion`,
  '#licencias-protocolos': `${LEGACY_LICENSES_BASE_PATH}/protocolo`,
  '#licencias-equipo': `${LEGACY_LICENSES_BASE_PATH}/equipo`,
  '#licencias-diagnostico': `${LEGACY_LICENSES_BASE_PATH}/diagnostico`,
};

let currentAbort: AbortController | null = null;
let previousFocus: HTMLElement | null = null;
let searchDebounce: number | undefined;

type NavigationSearchEntry = {
  anchor: HTMLAnchorElement;
  id: string;
  label: string;
  description: string;
  section: string;
  searchText: string;
};

export function resolveLegacyLicensesRoute(pathname: string, hash: string): string | null {
  return normalizePath(pathname) === LEGACY_LICENSES_BASE_PATH
    ? LEGACY_LICENSES_HASH_ROUTES[hash.toLocaleLowerCase('es-AR')] ?? null
    : null;
}

function redirectLegacyLicensesHash(): boolean {
  const target = resolveLegacyLicensesRoute(window.location.pathname, window.location.hash);
  if (!target) return false;

  window.location.replace(target);
  return true;
}

function isPlainLeftClick(event: MouseEvent): boolean {
  return event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;
}

function shouldHandle(anchor: HTMLAnchorElement, event: MouseEvent): boolean {
  if (event.defaultPrevented || !isPlainLeftClick(event)) return false;
  if (anchor.target && anchor.target !== '_self') return false;
  if (anchor.hasAttribute('download')) return false;
  if (anchor.dataset.noShellNav === 'true') return false;

  const href = anchor.getAttribute('href');
  if (!href || href.startsWith('#') || href.startsWith('mailto:') || href.startsWith('tel:')) return false;

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) return false;
  if (document.getElementById('patient-search-dialog')
      && ['/pacientes', '/pacientes/buscar'].includes(url.pathname)) return false;
  if (url.pathname === window.location.pathname && url.search === window.location.search) return false;

  return true;
}

function normalizePath(path: string): string {
  const normalized = path.trim().startsWith('/') ? path.trim() : `/${path.trim()}`;
  return normalized.length > 1 ? normalized.replace(/\/+$/, '') : normalized;
}

function pathMatches(currentPath: string, prefix: string): boolean {
  const current = normalizePath(currentPath);
  const candidate = normalizePath(prefix);
  return current === candidate || current.startsWith(`${candidate}/`);
}

function anchorMatchPaths(anchor: HTMLAnchorElement): string[] {
  return (anchor.dataset.navMatchPaths ?? '')
    .split('|')
    .map(path => path.trim())
    .filter(Boolean);
}

function scrollSidebarToActive(sidebar: HTMLElement, activeLink: HTMLElement, behavior: ScrollBehavior) {
  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(() => {
      const sidebarRect = sidebar.getBoundingClientRect();
      const linkRect = activeLink.getBoundingClientRect();
      const target = sidebar.scrollTop + linkRect.top - sidebarRect.top - ((sidebar.clientHeight - linkRect.height) / 2);
      sidebar.scrollTo({ top: Math.max(0, target), behavior });
    });
  });
}
export function serverNavigationActiveId(source: Document): string | null {
  return source.querySelector<HTMLAnchorElement>(`${SIDEBAR_SELECTOR} a[data-nav-id][aria-current="page"]`)?.dataset.navId ?? null;
}

function syncSidebarActive(url: URL, behavior: ScrollBehavior = 'smooth', canonicalId?: string | null) {
  const sidebar = document.querySelector<HTMLElement>(SIDEBAR_SELECTOR);
  if (!sidebar) return;

  sidebar.querySelectorAll<HTMLElement>('.active').forEach(el => el.classList.remove('active'));
  sidebar.querySelectorAll<HTMLElement>('[aria-current="page"]').forEach(el => el.removeAttribute('aria-current'));

  const links = Array.from(sidebar.querySelectorAll<HTMLAnchorElement>('a[data-nav-id][href]'));
  const exact = links.find(link => {
    const linkUrl = new URL(link.href, window.location.href);
    return linkUrl.pathname === url.pathname && linkUrl.search === url.search;
  });
  const matches = links
    .map(link => ({ link, score: anchorMatchPaths(link).filter(path => pathMatches(url.pathname, path)).sort((a, b) => b.length - a.length)[0]?.length ?? 0 }))
    .filter(match => match.score > 0)
    .sort((a, b) => b.score - a.score);
  // The server uses controller, action and permission-aware route rules. Its
  // selection also builds breadcrumbs, and must win over a broad URL prefix.
  const byPath = canonicalId !== undefined
    ? links.find(link => link.dataset.navId === canonicalId)
    : exact ?? matches[0]?.link;
  if (!byPath) return;

  const item = byPath.closest<HTMLElement>('li');
  item?.classList.add('active');
  byPath.setAttribute('aria-current', 'page');

  const group = byPath.closest<HTMLElement>('.nav-group');
  if (group) {
    group.classList.add('is-open');
    group.classList.remove('is-collapsed');
    group.querySelector<HTMLElement>('.nav-group__head')?.setAttribute('aria-expanded', 'true');
    persistExpandedSections();
  }

  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  scrollSidebarToActive(sidebar, byPath, prefersReducedMotion ? 'auto' : behavior);
}

function revealNavigationItem(item: HTMLElement) {
  const prefersReducedMotion = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
  item.scrollIntoView({
    block: 'nearest',
    inline: 'nearest',
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
  });
}

function readSet(key: string): Set<string> {
  try {
    const value = JSON.parse(localStorage.getItem(key) || '[]');
    return new Set(Array.isArray(value) ? value.filter(item => typeof item === 'string') : []);
  } catch {
    return new Set();
  }
}

function persistExpandedSections() {
  const expanded = Array.from(document.querySelectorAll<HTMLElement>(`${SIDEBAR_SELECTOR} .nav-group`))
    .filter(group => !group.classList.contains('is-collapsed'))
    .map(group => group.dataset.group)
    .filter((group): group is string => !!group);
  try { localStorage.setItem(EXPANDED_SECTIONS_KEY, JSON.stringify(expanded)); } catch { /* storage is optional */ }
}

function initNavigationGroups() {
  const sidebar = document.querySelector<HTMLElement>(SIDEBAR_SELECTOR);
  if (!sidebar || sidebar.dataset.navGroupsReady === 'true') return;
  sidebar.dataset.navGroupsReady = 'true';

  const storedExpanded = readSet(EXPANDED_SECTIONS_KEY);
  const hasStoredState = localStorage.getItem(EXPANDED_SECTIONS_KEY) !== null;
  sidebar.querySelectorAll<HTMLElement>('.nav-group').forEach(group => {
    const id = group.dataset.group;
    const head = group.querySelector<HTMLButtonElement>('.nav-group__head');
    if (!id || !head) return;

    const active = group.classList.contains('is-open');
    const expanded = active || !hasStoredState || storedExpanded.has(id);
    group.classList.toggle('is-collapsed', !expanded);
    head.setAttribute('aria-expanded', String(expanded));

    head.addEventListener('click', () => {
      const nextCollapsed = group.classList.toggle('is-collapsed');
      group.classList.toggle('is-open', !nextCollapsed);
      head.setAttribute('aria-expanded', String(!nextCollapsed));
      persistExpandedSections();
    });
  });
}

function setSidebarOpen(open: boolean, restoreFocus = false) {
  const sidebar = document.querySelector<HTMLElement>(SIDEBAR_SELECTOR);
  const toggle = document.querySelector<HTMLButtonElement>('#sidebar-toggle');
  const overlay = document.querySelector<HTMLButtonElement>('#sidebar-overlay');
  if (!sidebar || !toggle) return;

  sidebar.classList.toggle('open', open);
  document.body.classList.toggle('sidebar-open', open);
  toggle.setAttribute('aria-expanded', String(open));
  if (overlay) overlay.tabIndex = open ? 0 : -1;

  if (open) {
    previousFocus = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    sidebar.querySelector<HTMLElement>('a[href], button')?.focus({ preventScroll: true });
  } else if (restoreFocus) {
    previousFocus?.focus({ preventScroll: true });
    previousFocus = null;
  }
}

function initResponsiveSidebar() {
  const toggle = document.querySelector<HTMLButtonElement>('#sidebar-toggle');
  const overlay = document.querySelector<HTMLButtonElement>('#sidebar-overlay');
  if (!toggle || toggle.dataset.shellReady === 'true') return;
  toggle.dataset.shellReady = 'true';

  toggle.addEventListener('click', () => setSidebarOpen(true));
  overlay?.addEventListener('click', () => setSidebarOpen(false, true));
}

function initDesktopSidebarCollapse() {
  const toggle = document.querySelector<HTMLButtonElement>('#sidebar-collapse-toggle');
  if (!toggle || toggle.dataset.shellReady === 'true') return;
  toggle.dataset.shellReady = 'true';

  const collapsed = localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === 'true';
  document.body.classList.toggle('sidebar-collapsed', collapsed);
  toggle.setAttribute('aria-pressed', String(collapsed));
  toggle.setAttribute('aria-label', collapsed ? 'Expandir menú lateral' : 'Colapsar menú lateral');

  toggle.addEventListener('click', () => {
    const next = !document.body.classList.contains('sidebar-collapsed');
    document.body.classList.toggle('sidebar-collapsed', next);
    toggle.setAttribute('aria-pressed', String(next));
    toggle.setAttribute('aria-label', next ? 'Expandir menú lateral' : 'Colapsar menú lateral');
    try { localStorage.setItem(SIDEBAR_COLLAPSED_KEY, String(next)); } catch { /* storage is optional */ }
  });
}

function focusTrap(event: KeyboardEvent) {
  if (event.key === 'Escape') {
    if (document.body.classList.contains('sidebar-open')) setSidebarOpen(false, true);
    return;
  }
  if (event.key !== 'Tab' || !document.body.classList.contains('sidebar-open')) return;

  const sidebar = document.querySelector<HTMLElement>(SIDEBAR_SELECTOR);
  if (!sidebar) return;
  const focusable = Array.from(sidebar.querySelectorAll<HTMLElement>('a[href], button:not([disabled])'));
  if (!focusable.length) return;
  const first = focusable[0];
  const last = focusable[focusable.length - 1];
  if (event.shiftKey && document.activeElement === first) {
    event.preventDefault();
    last.focus();
  } else if (!event.shiftKey && document.activeElement === last) {
    event.preventDefault();
    first.focus();
  }
}

function replaceDocumentStyles(nextDoc: Document, existing: Set<string>) {
  nextDoc.querySelectorAll<HTMLLinkElement>('head link[rel="stylesheet"][href]').forEach(link => {
    if (existing.has(link.href)) return;
    document.head.appendChild(link.cloneNode(true));
    existing.add(link.href);
  });
}

function replaceDocumentScripts(nextDoc: Document, existing: Set<string>) {
  nextDoc.querySelectorAll<HTMLScriptElement>('script[src]').forEach(script => {
    if (existing.has(script.src)) return;
    const clone = document.createElement('script');
    for (const attr of Array.from(script.attributes)) clone.setAttribute(attr.name, attr.value);
    clone.async = false;
    (script.parentElement?.tagName === 'HEAD' ? document.head : document.body).appendChild(clone);
    existing.add(script.src);
  });
}

function currentAssetUrls(): Set<string> {
  return new Set(
    Array.from(document.querySelectorAll<HTMLLinkElement | HTMLScriptElement>('link[href], script[src]'))
      .map(el => ('href' in el ? el.href : el.src)),
  );
}

function runInlineScripts(container: ParentNode) {
  container.querySelectorAll<HTMLScriptElement>('script:not([src])').forEach(oldScript => {
    const script = document.createElement('script');
    for (const attr of Array.from(oldScript.attributes)) script.setAttribute(attr.name, attr.value);
    script.text = oldScript.text;
    oldScript.replaceWith(script);
  });
}

function runDocumentPageScripts(nextDoc: Document) {
  const scripts = Array.from(nextDoc.body.querySelectorAll<HTMLScriptElement>('script:not([src])'))
    .filter(script => !script.closest(CONTENT_SELECTOR));
  for (const oldScript of scripts) {
    const script = document.createElement('script');
    for (const attr of Array.from(oldScript.attributes)) script.setAttribute(attr.name, attr.value);
    script.text = oldScript.text;
    document.body.appendChild(script);
  }
}

async function navigate(url: URL, pushState = true) {
  currentAbort?.abort();
  currentAbort = new AbortController();

  document.body.classList.add(NAVIGATING_CLASS);
  window.dispatchEvent(new CustomEvent('profint:navigation:start', { detail: { url: url.toString() } }));

  try {
    const response = await fetch(url, {
      headers: {
        Accept: 'text/html',
        'X-Requested-With': 'ProfintShellNavigation',
      },
      signal: currentAbort.signal,
      credentials: 'same-origin',
    });

    if (!response.ok) {
      window.dispatchEvent(new CustomEvent('profint:navigation:partial-failed', { detail: { status: response.status, url: url.toString() } }));
      window.location.assign(url.toString());
      return;
    }

    const html = await response.text();
    const nextDoc = new DOMParser().parseFromString(html, 'text/html');
    const nextBody = nextDoc.querySelector<HTMLElement>(CONTENT_SELECTOR);
    const currentBody = document.querySelector<HTMLElement>(CONTENT_SELECTOR);
    if (!nextBody || !currentBody) {
      window.location.assign(url.toString());
      return;
    }

    const existingAssets = currentAssetUrls();
    replaceDocumentStyles(nextDoc, existingAssets);
    replaceDocumentScripts(nextDoc, existingAssets);

    document.title = nextDoc.title || document.title;
    const nextTitle = nextDoc.querySelector<HTMLElement>(TITLE_SELECTOR)?.textContent;
    const currentTitle = document.querySelector<HTMLElement>(TITLE_SELECTOR);
    if (currentTitle && nextTitle) currentTitle.textContent = nextTitle;

    currentBody.replaceChildren(...Array.from(nextBody.childNodes).map(node => document.importNode(node, true)));
    runInlineScripts(currentBody);
    runDocumentPageScripts(nextDoc);
    syncSidebarActive(url, 'smooth', serverNavigationActiveId(nextDoc));
    initControls(currentBody);
    // Pages inserted by the shell bypass HTMX's initial document scan.
    htmx.process(currentBody);

    if (pushState) history.pushState({ profintShell: true }, '', url);
    setSidebarOpen(false);
    window.scrollTo({ top: 0, left: 0 });
    document.querySelector<HTMLElement>('#main-content')?.focus({ preventScroll: true });
    window.dispatchEvent(new CustomEvent('profint:navigation:ready', { detail: { url: url.toString(), root: currentBody } }));
  } catch (error) {
    if ((error as Error).name === 'AbortError') return;
    console.error('[profint-shell] navegación parcial falló', error);
    window.dispatchEvent(new CustomEvent('profint:navigation:partial-failed', { detail: { status: 0, url: url.toString() } }));
    window.location.assign(url.toString());
  } finally {
    document.body.classList.remove(NAVIGATING_CLASS);
  }
}

function normalizeSearchText(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('es-AR')
    .trim();
}

function navigationSearchIndex(): NavigationSearchEntry[] {
  return Array.from(document.querySelectorAll<HTMLAnchorElement>(`${SIDEBAR_SELECTOR} a[data-nav-id][href]`))
    .map(anchor => {
      const label = anchor.querySelector<HTMLElement>('span')?.textContent?.trim()
        || anchor.textContent?.trim()
        || anchor.title;
      const description = anchor.title?.trim() || '';
      const section = anchor.dataset.navSection?.trim() || '';
      const searchText = normalizeSearchText([
        anchor.dataset.navSearch,
        label,
        description,
        section,
      ].filter(Boolean).join(' '));

      return {
        anchor,
        id: anchor.dataset.navId || '',
        label,
        description,
        section,
        searchText,
      };
    })
    .filter(entry => entry.id && entry.label);
}

function scoreSearchEntry(entry: NavigationSearchEntry, terms: string[]): number {
  const label = normalizeSearchText(entry.label);
  const description = normalizeSearchText(entry.description);
  const section = normalizeSearchText(entry.section);

  return terms.reduce((score, term) => score
    + (label.startsWith(term) ? 100 : label.includes(term) ? 60 : 0)
    + (description.includes(term) ? 25 : 0)
    + (section.includes(term) ? 10 : 0), 0);
}

function renderSearchResults(input: HTMLInputElement) {
  const resultBox = document.querySelector<HTMLElement>('#navigation-search-results');
  if (!resultBox) return;
  const terms = normalizeSearchText(input.value).split(/\s+/).filter(Boolean);
  resultBox.replaceChildren();
  if (!terms.length) {
    resultBox.hidden = true;
    input.setAttribute('aria-expanded', 'false');
    return;
  }

  const results = navigationSearchIndex()
    .filter(entry => terms.every(term => entry.searchText.includes(term)))
    .sort((left, right) => scoreSearchEntry(right, terms) - scoreSearchEntry(left, terms)
      || left.label.localeCompare(right.label, 'es-AR'));

  if (!results.length) {
    const empty = document.createElement('div');
    empty.className = 'navigation-search-empty';
    empty.textContent = 'Sin funciones coincidentes';
    resultBox.appendChild(empty);
  } else {
    results.forEach((entry, index) => {
      const result = document.createElement('a');
      result.href = entry.anchor.href;
      result.className = 'navigation-search-result';
      result.setAttribute('role', 'option');
      result.dataset.searchIndex = String(index);
      result.dataset.navId = entry.id;

      const label = document.createElement('span');
      label.className = 'navigation-search-result__label';
      label.textContent = entry.label;
      result.appendChild(label);

      const metadata = [entry.section, entry.description].filter(Boolean).join(' · ');
      if (metadata) {
        const detail = document.createElement('span');
        detail.className = 'navigation-search-result__detail';
        detail.textContent = metadata;
        result.appendChild(detail);
      }

      result.addEventListener('click', event => {
        if (!shouldHandle(result, event)) return;
        event.preventDefault();
        resultBox.hidden = true;
        input.setAttribute('aria-expanded', 'false');
        void navigate(new URL(result.href, window.location.href));
      });
      resultBox.appendChild(result);
    });
  }
  resultBox.hidden = false;
  input.setAttribute('aria-expanded', 'true');
}

function isEditableTarget(target: EventTarget | null): boolean {
  if (!(target instanceof Element)) return false;
  return !!target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"]');
}

function hasSelectedText(): boolean {
  return !!window.getSelection?.()?.toString().trim();
}

function searchShortcutLabel(): string {
  const platform = (navigator as Navigator & { userAgentData?: { platform?: string } })
    .userAgentData?.platform ?? navigator.platform ?? navigator.userAgent;
  return /mac|iphone|ipad|ipod/i.test(platform) ? '⌘ K' : 'Ctrl K';
}

function focusNavigationSearch(input: HTMLInputElement) {
  input.focus({ preventScroll: true });
  input.select();
  renderSearchResults(input);
}

function initNavigationSearch() {
  const input = document.querySelector<HTMLInputElement>('[data-navigation-search]');
  if (!input || input.dataset.shellReady === 'true') return;
  input.dataset.shellReady = 'true';
  document.querySelectorAll<HTMLElement>('[data-navigation-search-shortcut]').forEach(shortcut => {
    shortcut.textContent = searchShortcutLabel();
  });

  input.addEventListener('input', () => {
    window.clearTimeout(searchDebounce);
    searchDebounce = window.setTimeout(() => renderSearchResults(input), 120);
  });
  input.addEventListener('keydown', event => {
    const results = Array.from(document.querySelectorAll<HTMLElement>('#navigation-search-results [role="option"]'));
    if (event.key === 'Escape') {
      input.value = '';
      renderSearchResults(input);
    } else if (event.key === 'ArrowDown' && results.length) {
      event.preventDefault();
      results[0].focus();
    } else if (event.key === 'Enter' && results.length) {
      event.preventDefault();
      results[0].click();
    }
  });
  document.addEventListener('click', event => {
    if (!(event.target instanceof Node) || input.parentElement?.contains(event.target)) return;
    const resultBox = document.querySelector<HTMLElement>('#navigation-search-results');
    if (resultBox) resultBox.hidden = true;
    input.setAttribute('aria-expanded', 'false');
  });
  document.addEventListener('keydown', event => {
    const isShortcut = (event.ctrlKey || event.metaKey)
      && !event.altKey
      && !event.shiftKey
      && event.key.toLocaleLowerCase('es-AR') === 'k';
    if (!isShortcut || isEditableTarget(event.target) || hasSelectedText()) return;

    event.preventDefault();
    focusNavigationSearch(input);
  });
}

function wireShellNavigation() {
  if (redirectLegacyLicensesHash()) return;
  if (document.documentElement.dataset.shellNavigationReady === 'true') return;
  document.documentElement.dataset.shellNavigationReady = 'true';

  initNavigationGroups();
  initResponsiveSidebar();
  initDesktopSidebarCollapse();
  initNavigationSearch();
  syncSidebarActive(new URL(window.location.href), 'auto', serverNavigationActiveId(document));
  document.addEventListener('keydown', focusTrap);

  document.addEventListener('click', event => {
    const target = event.target instanceof Element ? event.target : null;
    const anchor = target?.closest<HTMLAnchorElement>(`${SIDEBAR_SELECTOR} a[href]`);
    if (!anchor || !shouldHandle(anchor, event)) return;
    event.preventDefault();
    void navigate(new URL(anchor.href, window.location.href));
  });

  window.addEventListener('popstate', () => {
    void navigate(new URL(window.location.href), false);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', wireShellNavigation, { once: true });
} else {
  wireShellNavigation();
}
